import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './FAQ.css';

const FAQSection = styled.section`
  padding: 15rem 0 4rem 0;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 8rem 0 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AccentLine = styled(motion.div)`
  width: 60px;
  height: 3px;
  background: #FF0707;
  margin-bottom: 3rem;
`;

const FAQContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const FAQItem = styled(motion.div)`
  background: white;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const FAQQuestion = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #f8fafc;
  }
`;

const QuestionText = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
`;

const ChevronIcon = styled.div`
  color: #FF0707;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 1.5rem 1.5rem;
  color: #64748b;
  line-height: 1.6;
  font-size: 1rem;
`;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(1); // Start with second FAQ open

  const faqs = [
    {
      question: "What payment methods are accepted?",
      answer: "On Street Parking is to be paid using Debit/Credit Card, additional payment methods will be added. Off Street Parking can be paid by Cash as Pay Stations, or Debit/Credit Card"
    },
    {
      question: "How far in advance can I pay for parking?",
      answer: "Off- Street Parking locations allow for extended parking. On-Street Parking is prepaid for a maximum of 1 hour."
    },
    {
      question: "How do I enter and exit the car park?",
      answer: "Off-Street Parking Locations are controlled by Boom gates, on entry you will be required to get a ticket from the entry station which will be validated for your exit."
    },
    {
      question: "Do I get allocated a specific parking space?",
      answer: "Designated parking is available for those who wish to pay for extended periods in advance in off-street parking locations, please contact us for further details."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <FAQSection>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Top FAQ's
        </SectionTitle>
        
        <AccentLine
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        />

        <FAQContainer>
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <FAQQuestion onClick={() => toggleFAQ(index)}>
                <QuestionText>{faq.question}</QuestionText>
                <ChevronIcon>
                  {openIndex === index ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </ChevronIcon>
              </FAQQuestion>
              
              {openIndex === index && (
                <FAQAnswer
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {faq.answer}
                </FAQAnswer>
              )}
            </FAQItem>
          ))}
        </FAQContainer>
      </Container>
    </FAQSection>
  );
};

export default FAQ; 